import React from 'react';
import { Link } from 'react-router-dom';
import { Select, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
    Table,
    Input,
    Button,
    Text,
    ButtonHierarchies,
    EmptyState,
    InputSizes,
} from '@clockout-dev/design-elements';
import { formatLongDate } from '../../../utils/dates';
import styles from './styles/cohort-users-drawer.module.scss';

const TITLES = {
    opted_in: 'Opted-In Users',
    enrolled: 'Enrolled Users',
    active: 'Active Users',
    engaged: 'Engaged Users',
    active_advancers: 'Active Users',
    funnel_opted_in: 'Opted-In Users',
    funnel_no_terms: 'Did Not Accept Terms',
    funnel_no_payroll: 'No Payroll Link',
    funnel_pinwheel_error: 'Pinwheel Error',
    funnel_verifying_payroll_data: 'Verifying Payroll Data',
    funnel_ownership_failed: 'Ownership Verification Failed',
    funnel_enrolled: 'Enrolled Users',
};

const SORT_OPTIONS = [
    { value: 'name', label: 'Name (A–Z)' },
    { value: 'last_active_desc', label: 'Last active · Newest' },
    { value: 'last_active_asc', label: 'Last active · Oldest' },
];

const columns = [
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name, row) =>
            row.user_id ? (
                <Link
                    to={`/users/user?userId=${encodeURIComponent(row.user_id)}`}
                    className={styles.userLink}
                >
                    {name || '—'}
                </Link>
            ) : (
                <Text as="span" size="s" className={styles.cellStrong}>{name || '—'}</Text>
            ),
    },
    {
        title: 'Last Active',
        dataIndex: 'last_active',
        key: 'last_active',
        render: (value) => <Text as="span" size="s" className={styles.cellMuted}>{formatLongDate(value)}</Text>,
    },
    {
        title: 'User ID',
        dataIndex: 'user_id',
        key: 'user_id',
        render: (value) => <Text as="span" size="s" className={styles.cellFaint}>{value}</Text>,
    },
];

/**
 * Presentational drill-down drawer for an adoption card's user list. Slides in
 * from the right. All state, fetching, filtering, sorting and export live in the
 * dashboard controller and arrive via the `cohort` prop — this component only
 * renders.
 */
function CohortUsersDrawer({ cohort = {} }) {
    const {
        open = false,
        metric,
        count = 0,
        rows = [],
        loading = false,
        query = '',
        sortBy = 'name',
        onSearch,
        onSort,
        onExport,
        onClose,
    } = cohort;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={640}
            destroyOnHidden
            rootClassName={styles.drawer}
            title={
                <Text as="span" className={styles.title}>
                    {TITLES[metric] || 'Users'}
                    {!loading ? <span className={styles.count}>{count}</span> : null}
                </Text>
            }
            extra={
                <Button
                    hierarchy={ButtonHierarchies.SECONDARY}
                    onClick={onExport}
                    disabled={loading || rows.length === 0}
                    icon={<DownloadOutlined />}
                    className={styles.exportBtn}
                >
                    Export
                </Button>
            }
        >
            <div className={styles.body}>
                <div className={styles.toolbar}>
                    <Input
                        size={InputSizes.SMALL}
                        allowClear
                        placeholder="Search by name or user ID…"
                        value={query}
                        onChange={(e) => onSearch(e.target.value)}
                        classNames={{ root: styles.search }}
                    />
                    <Select
                        value={sortBy}
                        onChange={onSort}
                        options={SORT_OPTIONS}
                        className={styles.sort}
                        aria-label="Sort users"
                    />
                </div>

                <div className={styles.tableScroll}>
                    <Table
                        columns={columns}
                        dataSource={rows}
                        loading={loading}
                        rowKey="key"
                        pagination={false}
                        emptyState={<EmptyState title="No users for this period." />}
                    />
                </div>
            </div>
        </Drawer>
    );
}

export default CohortUsersDrawer;
